import ReactDOM from "react-dom/client";
import { getPatientHistoryQueryKeys } from "../apis/parchi/use-get-patient-history";
import componentRegister from "../component-register";
import queryClient from "../instances/react-query-client";
import { ApplicationWrapper } from "../main";
import { HprHfrOnboardingModal } from "../organisms/hpr-hfr-onboarding/hpr-hfr-onboarding-modal";
import StethoReceiptPopup from "../organisms/receipt/stetho-receipt-popup";
import SaQuestionnaireDrawer from "../organisms/sa-questionnaire/sa-questionnaire-drawer";
import { ContainersExported, RendererParams } from "../react-app-env";
import useHprHfrOnboardingStore from "../stores/hpr-hfr-dhis-onboarding";
import { OnboardingNavigationStates } from "../stores/hpr-hfr-dhis-onboarding/enums";
import useQueueStore from "../stores/queue-store";
import {
  BILLING_POPUP_ID,
  BillingPopupEvent,
  DestroyContainerEvent,
  EKA_EVENT,
  ELIXIR_POPUP_MOUNT,
  EVENT_TYPES,
  IncomingEvents,
  OpenHprHfrOnboardingWithAadharPresent,
  OpenSaQuestionnaireListEvent,
  PresentAadhaarData,
  QUESTIONNAIRE_CONTAINER_ID,
} from "./bridges";
import { dispatch } from "./bridges/events/dispatch";
import { exposeBridgeFunctions } from "./exposed-functions";
import getB from "./bridges/EkaBridge";
import { offlineAptToAppointment } from "../controllers/appointments/utils/converter-utilts";
import { getAppointmentById, getMe } from "./bridges/methods";

const containersCreated: Map<string, ReactDOM.Root> = new Map();

export const destroyContainerEventListener: EventListener = (e) => {
  console.log("In event listener", "before check");
  const { detail } = e as CustomEvent<DestroyContainerEvent>;
  const containerToDestory = detail.containerId;
  if (
    detail.type === "DESTROY_CONTAINER" &&
    containersCreated.has(containerToDestory)
  ) {
    console.log("In event listener", "check passed");
    const componentRoot = containersCreated.get(containerToDestory);

    if (!!componentRoot) {
      console.log("Node found, unmounting node at", containerToDestory);
      componentRoot.unmount();
      containersCreated.delete(containerToDestory);
    }
  }
};

export function setupComponentExposer() {
  const containerKeys = Object.keys(componentRegister);
  for (let i = 0; i < containerKeys.length; i++) {
    const componentKeyName = containerKeys[i] as ContainersExported;
    const Component = componentRegister[componentKeyName];
    window[componentKeyName] = function (props: RendererParams) {
      const container = document.getElementById(props.containerId);

      if (container) {
        if (props.containerId && !containersCreated.has(props.containerId)) {
          const componentRoot = ReactDOM.createRoot(container);
          containersCreated.set(props.containerId, componentRoot);
        }

        const componentRoot = containersCreated.get(props.containerId);

        componentRoot?.render(
          <ApplicationWrapper>
            <Component {...props} />
          </ApplicationWrapper>
        );
      } else {
        console.error(
          `Failed to find container to mount component ${componentKeyName}`
        );
      }
    };
  }
}

let PopupReactNode: ReactDOM.Root | null = null;

const onEkaEvents: EventListener = (e: Event): void => {
  const { detail } = e as CustomEvent<IncomingEvents>;

  switch (detail.type) {
    case EVENT_TYPES.DESTROY_CONTAINER:
      destroyContainerEventListener(e);
      break;
    case EVENT_TYPES.BILLING_POPUP:
    case EVENT_TYPES.REQUEST_PAYMENT:
      const { detail } = e as CustomEvent<BillingPopupEvent>;
      const { data } = detail;

      PopupReactNode = ReactDOM.createRoot(
        document.getElementById(BILLING_POPUP_ID) as HTMLElement
      );
      PopupReactNode?.render(
        <ApplicationWrapper>
          <StethoReceiptPopup {...data} />
        </ApplicationWrapper>
      );
      break;

    case EVENT_TYPES.CLOSE_BILLING_POPUP:
      PopupReactNode?.unmount();
      break;

    case EVENT_TYPES.HPR_HFR_ONBOARDING_AADHAR_PRESENT:
      const { detail: aadhaarDetail } =
        e as CustomEvent<OpenHprHfrOnboardingWithAadharPresent>;

      PopupReactNode = ReactDOM.createRoot(
        document.getElementById(ELIXIR_POPUP_MOUNT) as HTMLElement
      );

      useHprHfrOnboardingStore
        .getState()
        .setAadhaarNumber(aadhaarDetail.data.aadhaar);
      useHprHfrOnboardingStore
        .getState()
        .setActiveScreen(OnboardingNavigationStates.AADHAR);
      useHprHfrOnboardingStore.getState().setOpenModal(true);

      PopupReactNode?.render(
        <ApplicationWrapper>
          <HprHfrOnboardingModal />
        </ApplicationWrapper>
      );
      break;

    case EVENT_TYPES.HPR_HFR_ONBOARDING:
      PopupReactNode = ReactDOM.createRoot(
        document.getElementById(ELIXIR_POPUP_MOUNT) as HTMLElement
      );

      useHprHfrOnboardingStore
        .getState()
        .setActiveScreen(OnboardingNavigationStates.AADHAR);
      useHprHfrOnboardingStore.getState().setOpenModal(true);

      PopupReactNode?.render(
        <ApplicationWrapper>
          <HprHfrOnboardingModal />
        </ApplicationWrapper>
      );
      break;

    case EVENT_TYPES.CLOSE_HPR_HFR_ONBOARDING:
      PopupReactNode?.unmount();
      break;

    case EVENT_TYPES.ADD_MH_QUEUE:
      const whoAmI = queryClient.getQueryData(["get-who-am-i"], {
        queryKey: ["get-who-am-i"],
        exact: true,
      }) as any;
      queryClient.invalidateQueries(
        getPatientHistoryQueryKeys({
          doctorOid: whoAmI?.["doc-id"],
          patientOid: useQueueStore.getState().selectedPatient?.oid,
        })
      );
      useQueueStore.getState().setShowAddMedicalHistoryPopup(false);
      break;

    case EVENT_TYPES.CLOSE_MH_POPUP:
      useQueueStore.getState().setShowAddMedicalHistoryPopup(false);
      break;

    case EVENT_TYPES.DR_SWITCH_EVENT:
      queryClient.clear();
      break;

    case EVENT_TYPES.SHOW_QUESTIONNAIRE_LIST:
      const { detail: questionnarieDetail } =
        e as CustomEvent<OpenSaQuestionnaireListEvent>;
      const { data: questionnaireData } = questionnarieDetail;
      getAppointmentById(questionnaireData.apt.aid).then((response) => {
        if (!response?.patient_oid) {
          return;
        }
        getB()
          .getIdb(getMe().user.oid)
          .table("patients")
          .get(response?.patient_oid)
          .then((patient) => {
            useQueueStore
              .getState()
              .setSelectedAppointment(
                offlineAptToAppointment(response, patient)
              );
          }).catch((error) => {
            console.error("Error fetching patient from IndexedDB:", error);
          });
      });
      useQueueStore.getState().setIsQuestionnaireDrawerOpenedFromPad(true);
      useQueueStore.getState().setOpenQuestionnaireDrawer(true);

      PopupReactNode = ReactDOM.createRoot(
        document.getElementById(QUESTIONNAIRE_CONTAINER_ID) as HTMLElement
      );

      PopupReactNode?.render(
        <ApplicationWrapper>
          <SaQuestionnaireDrawer />
        </ApplicationWrapper>
      );
      break;

    case EVENT_TYPES.CLOSE_QUESTIONNAIRE_LIST:
      PopupReactNode?.unmount();
      break;
  }
};

const postMessageEvent = (
  event: MessageEvent<{
    type: EVENT_TYPES.HPR_HFR_ONBOARDING_AADHAR_PRESENT;
    data: unknown;
  }>
) => {
  if (event.data.type === EVENT_TYPES.HPR_HFR_ONBOARDING_AADHAR_PRESENT) {
    dispatch({
      type: EVENT_TYPES.HPR_HFR_ONBOARDING_AADHAR_PRESENT,
      data: event.data.data as PresentAadhaarData,
    });
  }
};

export function exposeApp() {
  window.addEventListener(EKA_EVENT, onEkaEvents);
  window.addEventListener("message", postMessageEvent);
  window.onbeforeunload = () => {
    window.removeEventListener(EKA_EVENT, onEkaEvents);
    window.removeEventListener("message", postMessageEvent);
  };

  setupComponentExposer();
  exposeBridgeFunctions();
}
